import { countOwnedTilesOfKind } from "./buildings";
import { allCannonsDestroyed, countCannons } from "./cannon";
import { getTile } from "./grid";
import type {
  BuildingTileKind,
  GameState,
  PlayerId,
  Tile,
  Unit,
  UnitKind,
  VictoryCondition,
} from "./types";

export function activeVictoryCondition(
  source: Pick<GameState, "victoryConditions">,
): VictoryCondition {
  return source.victoryConditions?.[0] ?? "rout";
}

export function countOwnedCities(tiles: readonly Tile[], owner: PlayerId): number {
  return countOwnedTilesOfKind({ tiles, kind: "city", owner });
}

export function countOwnedMonuments(tiles: readonly Tile[], owner: PlayerId): number {
  return countOwnedTilesOfKind({ tiles, kind: "monument", owner });
}

function countMonuments(tiles: readonly Tile[]): number {
  return tiles.filter((tile) => tile.kind === "monument").length;
}

export function ownsAllMonuments({
  tiles,
  owner,
}: {
  tiles: readonly Tile[];
  owner: PlayerId;
}): boolean {
  const total = countMonuments(tiles);
  if (total === 0) return false;
  return countOwnedMonuments(tiles, owner) === total;
}

export function countPlayerSupplyTrucks(units: readonly Unit[], owner: PlayerId): number {
  return units.filter((unit) => unit.owner === owner && unit.kind === "supplyTruck").length;
}

function countSupplyTrucksOnDropZones(state: GameState): number {
  return state.units.filter((unit) => {
    if (unit.owner !== "p1" || unit.kind !== "supplyTruck") return false;
    return getTile(state, unit.pos).kind === "dropZone";
  }).length;
}

export function allSupplyTrucksOnDropZones(state: GameState): boolean {
  const trucks = state.units.filter((unit) => unit.owner === "p1" && unit.kind === "supplyTruck");
  if (trucks.length === 0) return false;
  return trucks.every((truck) => getTile(state, truck.pos).kind === "dropZone");
}

export type ObjectiveProgressIcon =
  | { type: "building"; kind: BuildingTileKind; owner: PlayerId }
  | { type: "unit"; kind: UnitKind; owner: PlayerId };

export type ObjectiveProgress = {
  kind: VictoryCondition;
  label: string;
  count: number | null;
  target: number | null;
  icon: ObjectiveProgressIcon | null;
};

export function objectiveDescription(
  source: Pick<GameState, "victoryConditions" | "captureCityTarget" | "initialSupplyTruckCount">,
): string {
  const kind = activeVictoryCondition(source);
  if (kind === "capture") {
    return `Capture ${source.captureCityTarget ?? 0} cities.`;
  }
  if (kind === "monuments") {
    return "Capture all monuments.";
  }
  if (kind === "escort") {
    const truckCount = source.initialSupplyTruckCount ?? 0;
    if (truckCount > 1) {
      return "Escort all Supply Trucks to a drop zone.";
    }
    return "Escort the Supply Truck to a drop zone.";
  }
  if (kind === "cannon") {
    return "Destroy all cannons.";
  }
  return "Destroy all enemy units.";
}

function objectiveLabel(state: GameState): string {
  return `Objective: ${objectiveDescription(state).replace(/\.$/, "")}`;
}

function captureObjectiveProgress(state: GameState): ObjectiveProgress {
  return {
    kind: "capture",
    label: objectiveLabel(state),
    count: countOwnedCities(state.tiles, "p1"),
    target: state.captureCityTarget ?? 0,
    icon: { type: "building", kind: "city", owner: "p1" },
  };
}

function monumentsObjectiveProgress(state: GameState): ObjectiveProgress {
  return {
    kind: "monuments",
    label: objectiveLabel(state),
    count: countOwnedMonuments(state.tiles, "p1"),
    target: countMonuments(state.tiles),
    icon: { type: "building", kind: "monument", owner: "p1" },
  };
}

function cannonObjectiveProgress(state: GameState): ObjectiveProgress {
  const remaining = countCannons(state.tiles);
  const target = state.initialCannonCount ?? remaining;
  return {
    kind: "cannon",
    label: objectiveLabel(state),
    count: Math.max(0, target - remaining),
    target,
    icon: { type: "building", kind: "cannon", owner: "p2" },
  };
}

function escortObjectiveProgress(state: GameState): ObjectiveProgress {
  const target = state.initialSupplyTruckCount ?? countPlayerSupplyTrucks(state.units, "p1");
  return {
    kind: "escort",
    label: objectiveLabel(state),
    count: countSupplyTrucksOnDropZones(state),
    target,
    icon: { type: "unit", kind: "supplyTruck", owner: "p1" },
  };
}

export function objectiveProgress(state: GameState): ObjectiveProgress {
  const kind = activeVictoryCondition(state);
  if (kind === "capture") return captureObjectiveProgress(state);
  if (kind === "monuments") return monumentsObjectiveProgress(state);
  if (kind === "cannon") return cannonObjectiveProgress(state);
  if (kind === "escort") return escortObjectiveProgress(state);
  return {
    kind,
    label: objectiveLabel(state),
    count: null,
    target: null,
    icon: null,
  };
}

export { allCannonsDestroyed };
